import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AppValidators } from '../../../../core/validators/app-validators';
import constants from '../../../../utility/constants';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { CommonDropdown, GetAuctionStep1 } from '../../../../models';
import { LotService, AddLotModel, LoaderService } from '../../../../services';
import { ToastrService } from 'ngx-toastr';
import { AuctionTimeService } from '../../../../services/auction-time.service';
import {  Subscription } from 'rxjs';
export interface SearchLot {
  id: number;
  text: string;
  selected: boolean;
}

@Component({
  selector: 'app-bulk-lot-form',
  templateUrl: './bulk-lot-form.component.html',
  styleUrls: ['./bulk-lot-form.component.css'],
})
export class BulkLotFormComponent implements OnInit {
  @Input() auctionId: number;
  @Input() lotId: number = 0;
  @Input() auctionData: GetAuctionStep1;
  @Output('lotFormStep1Completed') step1Completed = new EventEmitter<any>();

  loading: boolean = false;
  publishLoading: boolean = false;
  submitting: boolean = false;

  minimumLotsToBeAdd: number = 2;

  private lotType: number = 2;

  bulkLotForm: FormGroup;

  fieldLengthShorter: number = constants.common.validations.maxLength10;
  fieldLength: number = constants.common.validations.maxLength50;
  fieldLengthLonger: number = constants.common.validations.maxLength8000;
  minAmount: number = 0;
  maxAmount: number = constants.common.bid.bidMaxAmount;
  minModifier: number = constants.common.modifier.min;
  maxModifier: number = constants.common.modifier.max;

  // mat-slider config
  label: boolean = true;
  steps: number = 1;
  tickIntervals: number = 10;

  // actual lots listing
  lotsForSearch: Array<SearchLot> = [];
  // filtered lots on search
  filteredLots: Observable<Array<SearchLot>>;
  // for chips
  selectedLots: Array<SearchLot> = [];

  isClosingBegin: boolean = false;
  private subscriptions: Array<Subscription> = [];
  constructor(
    private lotService: LotService,
    private loader: LoaderService,
    private auctionTimeService: AuctionTimeService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initiateForm();

    if (this.lotId) this.getLotDataById();
    else this.lotsForDD();
  }

  private initiateForm() {
    this.bulkLotForm = new FormGroup({
      lotNumber: new FormControl(null, [
        Validators.required,
        Validators.maxLength(this.fieldLengthShorter),
        AppValidators.checkBulkLotNumber,
      ]),
      searchLot: new FormControl(null, [
        Validators.maxLength(this.fieldLengthShorter),
      ]),
      description: new FormControl(null, [
        Validators.required,
        Validators.maxLength(this.fieldLengthLonger),
      ]),
      location: new FormControl(null, [
        Validators.required,
        Validators.maxLength(this.fieldLength),
      ]),
      category: new FormControl(null, [
        Validators.required,
        Validators.maxLength(this.fieldLength),
      ]),
      consignor: new FormControl(null, [
        Validators.maxLength(this.fieldLength),
      ]),
      startingPrice: new FormControl(null, [
        Validators.min(this.minAmount),
        Validators.max(this.maxAmount),
      ]),
      modifier: new FormControl(0, [
        Validators.min(this.minModifier),
        Validators.max(this.maxModifier),
      ]),
    });
  }

  private lotsForDD() {
    this.lotService
      .getLotsByAuction(this.auctionId, this.lotType)
      .toPromise()
      .then((response: CommonDropdown[]) => {
        this.lotFiltering(response);
      });
  }

  private _filter(lotNumber: string | null): SearchLot[] {
    return lotNumber
      ? this.lotsForSearch.filter((option) => option.text.includes(lotNumber))
      : this.lotsForSearch;
  }

  private lotFiltering(response: CommonDropdown[]) {
    this.lotsForSearch = response.map((lot) => ({
      id: lot.value,
      text: lot.text,
      selected: false,
    }));

    if (this.lotId) {
      this.selectedLots.forEach((eachLot) => {
        let lotItem = this.lotsForSearch.find((it) => it.id == eachLot.id);
        if (lotItem) lotItem.selected = true;
      });
    }

    this.filteredLots = this.bulkLotForm.controls.searchLot.valueChanges.pipe(
      startWith(null),
      map((lotNumber: string | null) => this._filter(lotNumber))
    );
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    let lot: SearchLot = event.option.value;
    lot.text = lot.text;
    lot.selected = true;
    let findedLot = this.lotsForSearch.find((it) => it.id === lot.id);
    if (findedLot) {
      findedLot.selected = true;
    }
    this.selectedLots.push(lot);
    this.bulkLotForm.controls.searchLot.reset();
  }

  removeLot(lot: SearchLot, lotItemIndex: number) {
    lot.selected = false;
    let findLot = this.lotsForSearch.find((it) => it.id === lot.id);
    if (findLot == undefined) {
      this.lotsForSearch.push(lot);
    }
    this.selectedLots.splice(lotItemIndex, 1);
  }

  private getLotDataById() {
    this.loader.showLoader(true);
    const vlots = this.lotService.viewLotById(this.lotId).subscribe(
      (res) => {
        this.mapDataToForm(res);
        this.lotsForDD();
        this.loader.showLoader(false);
      },
      () => {
        this.loader.showLoader(false);
      }
    );
    this.subscriptions.push(vlots);
  }

  mapDataToForm(lotData: any) {
    this.bulkLotForm.patchValue({
      lotNumber: lotData.lotNumber,
      description: lotData.description,
      location: lotData.location,
      category: lotData.category,
      consignor: lotData.consignor,
      startingPrice: lotData.openingPrice,
      modifier: lotData.modifier,
    });
    this.selectedLots = lotData.lots.map((lot) => ({
      id: lot.id,
      text: lot.lotNumber,
      selected: true,
    }));
    this.isClosingBegin = this.auctionTimeService.isLotClosingBegun(
      lotData?.lotEndTimeUtc
    );
  }

  private createAddLotModel(isPublishLot): AddLotModel {
    let formValues = this.bulkLotForm.getRawValue();
    let model: AddLotModel = {
      isUpdateAndPublish: isPublishLot,
      auctionId: this.auctionId,
      consignor: formValues.consignor,
      category: formValues.category,
      description: formValues.description,
      location: formValues.location,
      lotId: this.lotId || 0,
      lotNumber: formValues.lotNumber,
      lotType: this.lotType,
      lots: this.selectedLots.map((it) => it.id).join(','),
      modifier: formValues.modifier,
      openingPrice: formValues.startingPrice,
    };
    return model;
  }

  bulkLotFormSubmit(isPublishLot) {
    this.submitting = true;

    if (this.bulkLotForm.invalid) return;

    if (this.selectedLots.length < this.minimumLotsToBeAdd) return;

    this.submitting = false;
    isPublishLot ? (this.publishLoading = true) : (this.loading = true);

    const aplots = this.lotService
      .addUpdateLot(this.createAddLotModel(isPublishLot))
      .subscribe(
        (res) => {
          isPublishLot ? (this.publishLoading = false) : (this.loading = false);
          this.bulkLotForm.reset();
          this.stepCompleted(res);
        },
        (err) => {
          isPublishLot ? (this.publishLoading = false) : (this.loading = false);
          this.toastr.error(err, 'Error');
        }
      );
      this.subscriptions.push(aplots);
  }

  stepCompleted(lotData: any) {
    this.step1Completed.emit(lotData);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }
}
